import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import { useVoiceMode } from '../utils/voiceMode';
import { agentSocket } from '../services/agentSocket';
import { isReminderAsk, ReminderAskCard } from './Reminders';

// A chat surface that talks to an EXTERNAL agent through the agent bridge: the user
// posts to `${base}/thread/messages`, the agent answers asynchronously via the
// bridge reply route, and we poll `${base}/thread/messages?since=` for it. Used by
// both Chief of Staff (base="/cos") and the Front Desk / Ask AI (base="/askai").
const NAVY = '#1e3a5f';

// Does the agent's last message end by asking permission to do something? The
// confirm-first tools all finish this way ("…shall I go ahead?"), and on a phone,
// in a truck, typing "yes" to that is the slowest part of the whole exchange.
//
// Deliberately strict: it must READ as a question at the end AND use asking
// language, so a long answer that merely mentions the word "confirm" somewhere in
// the middle doesn't sprout a pair of buttons.
export function asksToConfirm(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  const tail = s.slice(-260);
  // Spelling out "yes or no" is already the whole question — nothing else needed.
  if (/\b(yes\s*\/\s*no|yes or no)\b/i.test(tail)) return true;
  return /\?\s*$/.test(s)
    && /\b(confirm|shall i|should i|want me to|go ahead|proceed|ok(?:ay)? to)\b/i.test(tail);
}

// Links in a reply become tappable (Pat, Sep 27 2026 — "take me to the fuel log"):
// a markdown link [Fuel log](/sup/shop?tab=driver), a bare app path like
// /sup/shop?tab=driver, or a full address on the app's own domain all open inside the
// app; any other https address opens in a new tab.
const APP_HOSTS = /^https?:\/\/(app\.titanelectricreno\.com|localhost)(?::\d+)?/i;
// No lookbehind here on purpose — older iPhones' Safari would refuse the whole file.
// A bare path is only taken when it starts the text or follows a space or bracket.
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)|(https?:\/\/[^\s<>)]+)|(\/(?:admin|sup|time|safety|safety-documents|dashboard|create-po|my-updates|ask|cos)(?:\/[\w.-]+)*(?:\?[\w=&.-]+)?)/g;
const bareOk = (s, at) => at === 0 || /[\s([]/.test(s[at - 1]);
function ChatLink({ href, label, navigate }) {
  const inApp = href.startsWith('/') || APP_HOSTS.test(href);
  const path = inApp ? href.replace(APP_HOSTS, '') || '/' : null;
  const style = { display: 'inline-block', margin: '1px 2px', padding: '2px 9px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontWeight: 700, textDecoration: 'none', cursor: 'pointer', fontSize: '0.95em' };
  if (inApp) return <button type="button" onClick={() => navigate(path)} style={{ ...style, font: 'inherit', fontWeight: 700 }}>{label || 'Take me there'} ›</button>;
  return <a href={href} target="_blank" rel="noreferrer" style={style}>{label || href.replace(/^https?:\/\//, '')} ↗</a>;
}
function MsgText({ text }) {
  const navigate = useNavigate();
  const withLinks = (s, keyBase) => {
    const out = []; let last = 0; let m; let k = 0;
    LINK_RE.lastIndex = 0;
    while ((m = LINK_RE.exec(s))) {
      if (m[4] && !bareOk(s, m.index)) continue;   // "/" inside a word or a date is not a link
      if (m.index > last) out.push(s.slice(last, m.index));
      const href = m[2] || m[3] || m[4]; const label = m[1] || null;
      // Trailing punctuation belongs to the sentence, not the address.
      const trimmed = href.replace(/[.,;:!?]+$/, ''); const tail = href.slice(trimmed.length);
      out.push(<ChatLink key={`${keyBase}-${k++}`} href={trimmed} label={label} navigate={navigate} />);
      if (tail) out.push(tail);
      last = m.index + m[0].length;
    }
    if (last < s.length) out.push(s.slice(last));
    return out;
  };
  return (
    <div>
      {String(text || '').split('\n').map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const r = parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{withLinks(p, `${i}-${j}`)}</strong> : <React.Fragment key={j}>{withLinks(p, `${i}-${j}`)}</React.Fragment>));
        return <div key={i} style={{ margin: '2px 0' }}>{r}</div>;
      })}
    </div>
  );
}

// Does a reply already point somewhere inside the app? Then the agent did its job
// and we add nothing under it.
// PO numbers named in a message — "PO 20229", "PO #20229", "P.O. 20229" (Ryan, Oct 18 2026:
// "when a shop person asks about a PO … take the employee to that PO"). No AI involved.
export function poNumbersIn(text) {
  const out = [];
  for (const m of String(text || '').matchAll(/\bP\.?\s?O\.?\s*(?:#|number|no\.?)?\s*(\d{4,6})\b/gi)) if (!out.includes(m[1])) out.push(m[1]);
  return out.slice(0, 3);
}

// Dig ticket numbers in a question — "dig ticket 2026101900201", "USA #2026-101-900201", or a
// bare 13-digit 20YYMMDD… number (Ryan, Oct 21 2026). No AI: the app looks the number up.
export function digTicketsIn(text) {
  const t = String(text || ''), out = [];
  const add = n => { const d = String(n).replace(/\D/g, ''); if (d.length >= 8 && d.length <= 16 && !out.includes(d)) out.push(d); };
  for (const m of t.matchAll(/\b(?:dig|usa|811|locate|one[- ]?call)\b[^\d\n]{0,20}(\d[\d\s-]{6,20}\d)/gi)) add(m[1]);
  for (const m of t.matchAll(/\b(20\d{11})\b/g)) add(m[1]);
  return out.slice(0, 3);
}
// "Plans for WO26140", "drawings for 2631" (Ryan, Oct 23 2026): a question about plans that names a
// job number. No AI: the app finds the job and hands over its Fieldwire plans.
export function planJobsIn(text) {
  const t = String(text || '');
  if (!/\b(plans?|drawings?|prints?|blue ?prints?|sheets?|plan ?set)\b/i.test(t)) return [];
  const out = [];
  for (const m of t.matchAll(/\b(WO\s?\d{4,6}|\d{4}-WO\d{3}|\d{4})\b/gi)) {
    const n = m[1].toUpperCase().replace(/\s/g, '');
    if (/^20[2-3]\d$/.test(n) && !/\b(job|project|wo|work order)\s*#?\s*$/i.test(t.slice(0, m.index))) continue;   // a year, not a job
    if (!out.includes(n)) out.push(n);
  }
  return out.slice(0, 3);
}
// What a found ticket says: "Dig ticket 2026101900201 is on 2631 — RTC Pyramid Highway (📍 …) · called in 2026-10-19."
function digTicketLine(x) {
  const num = x.project_number || '';
  const name = String(x.project_name || '');
  const job = [num, name && name !== num ? (num && name.startsWith(num) ? name.slice(num.length).replace(/^\s*-?\s*/, '') : name) : ''].filter(Boolean).join(' — ') || 'a job';
  return `Dig ticket ${x.usa_number} is on ${job}${x.location ? ` (📍 ${x.location})` : ''}${x.called_in_at ? ` · called in ${String(x.called_in_at).slice(0, 10)}` : ''}${x.dug_at ? ' · dug' : ''}${x.closed ? ' · card closed' : ''}.`;
}

export function hasAppLink(text) {
  const s = String(text || ''); let m; LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(s))) {
    if (m[4] && !bareOk(s, m.index)) continue;
    const href = m[2] || m[3] || m[4];
    if (href.startsWith('/') || APP_HOSTS.test(href)) return true;
  }
  return false;
}
// "Where is… / how do I… / take me to…" — the questions a Company Answer is for.
const LOCATION_Q = /\b(where|how do|how can|how to|how would|find|locate|link|take me|show me|open|get to|go to)\b/i;
// The Company Answer that best fits a question, if it fits well enough to act on:
// a location-type question needs two points (one keyword hit, or a word in the
// office's question), anything else needs five. Returns the row with the path for
// this device, or null. Pat, Sep 27 2026: "on the how-do-I questions, a link to
// the location in the response" — and it appears whether or not the agent sent one.
export function pickAnswerHint(rows, userText, phone) {
  const top = Array.isArray(rows) && rows[0];
  if (!top) return null;
  const path = (phone ? top.phone_path : top.desktop_path) || top.desktop_path || top.phone_path;
  if (!path) return null;
  const score = Number(top.score || 0);
  if (!((LOCATION_Q.test(String(userText || '')) && score >= 2) || score >= 5)) return null;
  return { question: top.question, path };
}

// Answers first (Pat, Oct 5 2026): a typed question is checked against Company Answers
// before it goes to the AI. A good fit is answered on the spot — the office's own words and
// a Take me there — with 'Ask Titan AI instead' if it missed; nothing fits → straight to the
// AI as before. Same bar as the hint under replies: a where-is / how-do-I question needs 2
// points, anything else 5.
export function pickCompanyAnswer(rows, userText) {
  const top = Array.isArray(rows) && rows[0];
  if (!top || !top.answer) return null;
  const score = Number(top.score || 0);
  return (LOCATION_Q.test(String(userText || '')) && score >= 2) || score >= 5 ? top : null;
}

// Questions under the box as you type (Ryan, Oct 21 2026): the office's Company Answers and the
// usual questions, narrowed by every word typed — the more you type, the fewer there are. A Company
// Answer is answered on the spot; anything else goes to the AI. Little words don't narrow anything.
const TYPEAHEAD_SKIP = new Set(['a', 'an', 'the', 'i', 'my', 'me', 'do', 'does', 'is', 'are', 'am', 'to', 'of', 'on', 'in', 'for', 'and', 'or', 'it', 'how', 'what', 'where', 'who', 'when', 'can', 'we', 'our', 'you', 'your', 'this', 'that', 'there', 'get', 'find', 'look', 'up']);
const MORE_QUESTIONS = [
  'What job am I on tomorrow?', 'Who am I scheduled with?', 'Where is my job site?', 'What materials are coming for my job?',
  'Has my PO arrived?', "What's left to do on my jobs?", 'What job is dig ticket … for?', 'When does my USA dig expire?',
];
export function typeaheadMatches(input, answers, starters) {
  const words = String(input || '').toLowerCase().split(/[^a-z0-9#]+/).filter(w => w.length >= 2 && !TYPEAHEAD_SKIP.has(w));
  if (!words.length) return [];
  const hit = text => words.every(w => text.includes(w));
  const fromAnswers = (answers || []).filter(a => hit(`${a.question} ${a.keywords || ''}`.toLowerCase())).map(a => ({ kind: 'answer', text: a.question, row: a }));
  const seen = new Set(fromAnswers.map(x => x.text.toLowerCase()));
  const fromAsk = [...new Set([...(starters || []), ...MORE_QUESTIONS])].filter(q => !seen.has(q.toLowerCase()) && hit(q.toLowerCase())).map(q => ({ kind: 'ask', text: q }));
  return [...fromAnswers, ...fromAsk].slice(0, 5);
}

export default function BridgeChat({
  base, title, subtitle, placeholder = 'Type a message…', agentLabel = 'Assistant',
  emptyTitle = 'Start the conversation.', emptyBody = '', accent = '#4f46e5',
  icon = '💬', iconBg = NAVY, waitingText = 'Working on it…', pill = null,
  suggestions = null, onBack = null, answersFirst = false, suggestReplies = false, typeahead = false,
}) {
  // Company Answers given in place of the AI — on this screen only, never sent to the thread.
  const [local, setLocal] = useState([]);   // [{ id, q, row, at, state: 'open' | 'done' }]
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // Company Answers for the as-you-type list — read once.
  const [allAnswers, setAllAnswers] = useState([]);
  useEffect(() => { if (typeahead) apiFetch('/answers').then(d => setAllAnswers(Array.isArray(d) ? d : [])).catch(() => {}); }, [typeahead]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [err, setErr] = useState('');
  // Company-Answer "Take me there" under an agent reply, keyed by the reply's id
  // (null = looked, nothing fitting). Only the newest exchange is ever looked up.
  const [hints, setHints] = useState({});
  const hintsRef = useRef({});
  const navigate = useNavigate();
  const onPhone = !window.location.pathname.startsWith('/admin');
  const bottomRef = useRef();
  const inputRef = useRef();
  // Hands-free mode. The agent answers asynchronously, so the loop is driven by
  // the reply actually arriving rather than by the send returning.
  const voice = useVoiceMode({ onUtterance: text => send(text) });
  const spokenRef = useRef(new Set());
  const lastTs = useRef('1970-01-01');
  const convId = useRef(null);                 // matched against the socket's nudge
  // The agent answers in tens of seconds, not tenths. A bare row of dots looks the
  // same at five seconds and at fifty, so the wait is counted out loud.
  const [waitingSince, setWaitingSince] = useState(0);
  const [clockTick, setClockTick] = useState(0);
  const stopWaiting = () => { setWaiting(false); setWaitingSince(0); };

  useEffect(() => {
    setLoading(true);
    apiFetch(`${base}/thread`)
      .then(d => { const m = d.messages || []; convId.current = d.conversationId || null; setMessages(m); if (m.length) lastTs.current = m[m.length - 1].created_at; })
      .catch(e => setErr(e?.error || 'Could not open this chat.'))
      .finally(() => setLoading(false));
  }, [base]);

  const pullNew = React.useCallback(async () => {
    try {
      const d = await apiFetch(`${base}/thread/messages?since=${encodeURIComponent(lastTs.current)}`);
      const fresh = d.messages || [];
      if (!fresh.length) return;
      lastTs.current = fresh[fresh.length - 1].created_at;
      setMessages(prev => { const have = new Set(prev.map(m => m.id)); return [...prev, ...fresh.filter(m => !have.has(m.id))]; });
      if (fresh.some(m => m.role === 'agent')) { setWaiting(false); setWaitingSince(0); }
    } catch {}
  }, [base]);

  // The socket tells us a reply landed; the poll is what catches it if the socket
  // is down. Both end up in the same authenticated fetch — the broadcast itself is
  // never read, because it reaches every client on the server.
  useEffect(() => {
    const off = agentSocket.subscribe(msg => {
      if (!convId.current || msg.conversationId !== convId.current) return;
      pullNew();
    });
    const t = setInterval(pullNew, 3000);
    return () => { off(); clearInterval(t); };
  }, [pullNew]);

  // Tick only while something is outstanding — no timer running on an idle screen.
  useEffect(() => {
    if (!waiting || !waitingSince) return;
    setClockTick(Date.now());
    const t = setInterval(() => setClockTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting, waitingSince]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, waiting]);

  // Wipe this thread and start fresh — long threads get unscrollable on a phone.
  async function clearThread() {
    if (clearing) return;
    if (!window.confirm('Clear this conversation?\n\nThe messages are deleted and you start fresh. This only affects your own thread.')) return;
    setClearing(true);
    try {
      await apiFetch(`${base}/thread/messages`, { method: 'DELETE' });
      setMessages([]);
      lastTs.current = new Date().toISOString();   // don't re-poll the deleted ones
      setErr('');
    } catch (e) { setErr(e?.error || e?.message || 'Could not clear the conversation.'); }
    setClearing(false);
  }

  // Typed questions (and Enter / the arrow) come here: Company Answers first, when asked to.
  async function ask(text) {
    const body = (text || input).trim();
    if (!body || sending) return;
    // "Remind me …" is the app's, not the AI's (Ryan, Oct 26 2026): what and when it read, editable, Save.
    if (isReminderAsk(body)) {
      let parsed = null;
      try { parsed = await apiFetch('/reminders/parse', { method: 'POST', body: JSON.stringify({ text: body }) }); } catch { parsed = null; }
      if (parsed) {
        setInput('');
        if (inputRef.current) inputRef.current.style.height = 'auto';
        setLocal(l => [...l.map(x => ({ ...x, state: 'done' })), { id: 'rm_' + Math.random().toString(36).slice(2), q: body, kind: 'reminder', parsed, at: new Date().toISOString(), state: 'open' }]);
        return;
      }
    }
    // A dig ticket number answers itself: which job it is on, with Take me there to its card.
    const tickets = digTicketsIn(body);
    if (tickets.length) {
      const found = [];
      for (const n of tickets) { try { found.push(await apiFetch(`/usa-digs/lookup?ticket=${encodeURIComponent(n)}`)); } catch { /* fall through to the AI */ } }
      if (found.length) {
        setInput('');
        if (inputRef.current) inputRef.current.style.height = 'auto';
        const hit = found.find(r => r.found);
        const m = hit && hit.matches[0];
        const answer = found.map(r => (r.found ? r.matches.map(digTicketLine).join('\n') : `No dig ticket ${r.ticket} is on a USA Digs card in the app yet.`)).join('\n');
        const row = { question: hit ? 'Dig ticket' : 'Dig ticket not found', answer,
          desktop_path: m ? m.path : '/admin/workflows/usa_digs', phone_path: m ? m.phone_path : '/sup/workflows/usa_digs' };
        setLocal(l => [...l.map(x => ({ ...x, state: 'done' })), { id: 'ca_' + Math.random().toString(36).slice(2), q: body, row, at: new Date().toISOString(), state: 'open' }]);
        return;
      }
    }
    // Plans for a job number: its Fieldwire plans, straight away.
    const planJobs = planJobsIn(body);
    if (planJobs.length) {
      const found = [];
      for (const n of planJobs) { try { found.push(await apiFetch(`/plans/lookup?job=${encodeURIComponent(n)}`)); } catch { /* fall through */ } }
      if (found.some(r => r.found)) {
        setInput('');
        if (inputRef.current) inputRef.current.style.height = 'auto';
        const all = found.flatMap(r => r.matches);
        const links = [];
        const lines = all.map(m => {
          const job = `${m.project_number}${m.name && !String(m.name).startsWith(m.project_number) ? ' — ' + m.name : ''}`;
          if (m.fieldwire) links.push({ label: all.length > 1 ? `Plans: ${m.name || m.project_number}` : 'Open the plans in Fieldwire', href: m.fieldwire.plans_url });
          return m.fieldwire
            ? `${job}: ${m.fieldwire.sheets || 'its'} plan sheet${m.fieldwire.sheets === 1 ? '' : 's'} in Fieldwire — tap below to open them (the Fieldwire app opens if it's on your phone).`
            : `${job}: no Fieldwire project is set up for it yet${m.app_plans ? ` — ${m.app_plans} plan file${m.app_plans === 1 ? ' is' : 's are'} on the job page under Plans` : ' — ask the office to send its plans to Fieldwire'}.`;
        });
        if (all.length > 1) lines.unshift(`There are ${all.length} jobs on that number:`);
        const first = all[0];
        const row = { question: 'Plans', answer: lines.join('\n'), links,
          desktop_path: all.length === 1 ? first.path : '/admin/new-projects', phone_path: all.length === 1 ? first.phone_path : null };
        setLocal(l => [...l.map(x => ({ ...x, state: 'done' })), { id: 'ca_' + Math.random().toString(36).slice(2), q: body, row, at: new Date().toISOString(), state: 'open' }]);
        return;
      }
    }
    if (!answersFirst) return send(body);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    let row = null;
    try { row = pickCompanyAnswer(await apiFetch(`/answers?q=${encodeURIComponent(body.slice(0, 200))}`), body); } catch { row = null; }
    if (!row) return send(body);
    setLocal(l => [...l.map(x => ({ ...x, state: 'done' })), { id: 'ca_' + Math.random().toString(36).slice(2), q: body, row, at: new Date().toISOString(), state: 'open' }]);
  }
  function askAIInstead(item) { setLocal(l => l.filter(x => x.id !== item.id)); send(item.q); }

  async function send(text) {
    const body = (text || input).trim();
    if (!body || sending) return;
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';   // back to one line for the next message
    setSending(true);
    const optimistic = { id: 'tmp_' + Math.random().toString(36).slice(2), role: 'user', text: body, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    setWaiting(true);
    setWaitingSince(Date.now());
    try {
      const r = await apiFetch(`${base}/thread/messages`, { method: 'POST', body: JSON.stringify({ text: body }) });
      if (r?.created_at) lastTs.current = r.created_at;
      setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, id: r.id || m.id, created_at: r.created_at || m.created_at } : m));
    } catch (e) {
      stopWaiting();
      setMessages(prev => [...prev, { id: 'e' + Math.random(), role: 'agent', text: `Couldn't send that: ${e.message}`, created_at: new Date().toISOString() }]);
    } finally { setSending(false); setTimeout(() => inputRef.current?.focus(), 50); }
  }

  // Read out each new agent reply once, while voice mode is on. Keyed by message
  // id so a re-render or a poll returning the same message can't repeat it.
  useEffect(() => {
    if (!voice.active) return;
    const last = [...messages].reverse().find(m => m.role === 'agent');
    if (!last || spokenRef.current.has(last.id)) return;
    spokenRef.current.add(last.id);
    voice.speak(last.text);
  }, [messages, voice.active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ai = messages.length - 1;
    const reply = messages[ai];
    if (!reply || reply.role !== 'agent' || reply.id in hintsRef.current) return;
    let ui = ai - 1; while (ui >= 0 && messages[ui].role !== 'user') ui--;
    const asked = ui >= 0 ? messages[ui].text : '';
    hintsRef.current[reply.id] = null;   // claimed — one lookup per reply
    if (!asked || hasAppLink(reply.text) || !LOCATION_Q.test(asked)) return;
    apiFetch(`/answers?q=${encodeURIComponent(asked.slice(0, 200))}`)
      .then(rows => { const h = pickAnswerHint(rows, asked, onPhone); if (h) { hintsRef.current[reply.id] = h; setHints(x => ({ ...x, [reply.id]: h })); } })
      .catch(() => {});
  }, [messages, onPhone]);

  // The most likely answer, waiting in the box (Ryan, Oct 13 2026 — "put the most logical answer
  // in the text box ready for approval" on the Chief of Staff). The agent's own suggested_reply
  // when it sends one; otherwise, for a plain confirm question, "Yes, go ahead". Filled once per
  // reply, only into an empty box; typing or clearing it is the person's call.
  const [suggested, setSuggested] = useState(null);   // { id, text } of what's in the box
  const filledRef = useRef(new Set());
  useEffect(() => {
    if (!suggestReplies || waiting || sending || loading) return;
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'agent' || filledRef.current.has(lastMsg.id)) return;
    filledRef.current.add(lastMsg.id);
    const text = (lastMsg.suggested_reply || '').trim() || (asksToConfirm(lastMsg.text) ? 'Yes, go ahead' : '');
    if (!text) return;
    setInput(cur => {
      if (cur.trim()) return cur;
      setSuggested({ id: lastMsg.id, text });
      setTimeout(() => { const el = inputRef.current; if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 160) + 'px'; } }, 0);
      return text;
    });
  }, [messages, waiting, sending, loading, suggestReplies]);
  const showingSuggestion = !!(suggested && input === suggested.text);

  const waitedSec = waiting && waitingSince ? Math.max(0, Math.round((clockTick - waitingSince) / 1000)) : 0;
  // The starter questions step aside once you type — the as-you-type list takes over (Oct 21 2026).
  const showSuggestions = suggestions && messages.length === 0 && local.length === 0 && !loading && !err && !(typeahead && input.trim());
  // Only when the agent spoke last and it was a question awaiting a yes or a no —
  // never while a reply is still coming, and never once you've said something since.
  const last = messages[messages.length - 1];
  const pendingConfirm = !waiting && !sending && !loading && !err
    && last?.role === 'agent' && asksToConfirm(last.text);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, borderBottom: '1px solid var(--border, #e5e7eb)', marginBottom: 8 }}>
        {/* Back sits in the header rather than floating above it, so it's in the same
            place whichever way you came in, and it's a 44px target for a gloved thumb. */}
        {onBack && (
          <button onClick={onBack} aria-label="Back" title="Back"
            style={{ flexShrink: 0, width: 44, height: 44, marginLeft: -10, marginRight: -4, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--text-muted, #6b7280)', fontSize: 30, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>‹</button>
        )}
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: iconBg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text, #111)' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>{subtitle}</div>}
        </div>
        {messages.length > 0 && (
          <button onClick={clearThread} disabled={clearing} title="Clear this conversation"
            style={{ flexShrink: 0, background: 'none', border: '1px solid var(--border, #e5e7eb)', color: 'var(--text-muted, #6b7280)', borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 36 }}>
            {clearing ? '…' : 'Clear'}
          </button>
        )}
        {pill && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: pill.color, background: pill.bg, borderRadius: 999, padding: '4px 10px' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: pill.dot || pill.color }} />{pill.text}
          </span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 4px', minHeight: 0 }}>
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{err}</div>}
        {loading && !err && <div style={{ color: 'var(--text-muted, #6b7280)', fontSize: 14, padding: '20px 0' }}>Loading…</div>}
        {!loading && !err && messages.length === 0 && local.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px 16px' }}>
            <div style={{ fontSize: 30, marginBottom: 10 }}>{icon}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 4 }}>{emptyTitle}</div>
            {emptyBody && <div style={{ fontSize: 13 }}>{emptyBody}</div>}
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
            <div style={{ maxWidth: '78%', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5,
              background: m.role === 'user' ? NAVY : '#fff', color: m.role === 'user' ? '#fff' : '#1f2937',
              border: m.role === 'user' ? 'none' : '1px solid #eef2f7', boxShadow: m.role === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.04)' }}>
              {m.role !== 'user' && <div style={{ fontSize: 11, fontWeight: 800, color: accent, marginBottom: 5 }}>{agentLabel}</div>}
              <MsgText text={m.text} />
              {/* A reply that names a PO: open it — the phone page for the field and shop, the office page here. */}
              {m.role !== 'user' && !hasAppLink(m.text) && poNumbersIn(m.text).length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {poNumbersIn(m.text).map(n => (
                    <button key={n} type="button" onClick={() => navigate(onPhone ? `/shop/pos?po=${n}` : `/admin/purchase-orders?po=${n}`)}
                      style={{ padding: '4px 11px', borderRadius: 999, background: '#eff6ff', border: '1px solid #93c5fd', color: '#1e40af', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>🧾 Open PO {n} ›</button>
                  ))}
                </div>
              )}
              {hints[m.id] && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fde68a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#92400e', fontWeight: 700 }}>📘 {hints[m.id].question}</span>
                  <button type="button" onClick={() => navigate(hints[m.id].path)}
                    style={{ padding: '4px 11px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Take me there ›</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {local.filter(x => !messages.length || x.at >= messages[messages.length - 1].created_at || x.state === 'open').map(x => {
          if (x.kind === 'reminder') return (
            <React.Fragment key={x.id}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <div style={{ maxWidth: '78%', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: NAVY, color: '#fff' }}>{x.q}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                <div style={{ width: 'min(420px, 85%)', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: '#fffbeb', color: '#1f2937', border: '1px solid #fde68a' }}>
                  <ReminderAskCard asked={x.q} parsed={x.parsed} onAskAI={x.state === 'open' && !sending ? () => askAIInstead(x) : null} />
                </div>
              </div>
            </React.Fragment>
          );
          const path = (onPhone ? x.row.phone_path : x.row.desktop_path) || x.row.desktop_path || x.row.phone_path;
          return (
            <React.Fragment key={x.id}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <div style={{ maxWidth: '78%', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: NAVY, color: '#fff' }}>{x.q}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                <div style={{ maxWidth: '85%', padding: '11px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: '#fffbeb', color: '#1f2937', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309', marginBottom: 5 }}>📘 Company answer · {x.row.question}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{x.row.answer}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
                    {(x.row.links || []).map(lk => (
                      <a key={lk.href} href={lk.href} target="_blank" rel="noreferrer" style={{ padding: '6px 12px', borderRadius: 999, background: '#1e3a5f', border: '1px solid #1e3a5f', color: '#fff', fontWeight: 800, fontSize: 13, textDecoration: 'none' }}>📐 {lk.label} ↗</a>
                    ))}
                    {path && <button type="button" onClick={() => navigate(path)} style={{ padding: '6px 12px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Take me there ›</button>}
                    {x.state === 'open' && <button type="button" onClick={() => setLocal(l => l.map(y => y.id === x.id ? { ...y, state: 'done' } : y))} style={{ padding: '6px 12px', borderRadius: 999, background: '#fff', border: '1px solid #d1d5db', color: '#15803d', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>✓ That answers it</button>}
                    {x.state === 'open' && <button type="button" onClick={() => askAIInstead(x)} disabled={sending} style={{ padding: '6px 12px', borderRadius: 999, background: '#fff', border: `1px solid ${accent}`, color: accent, fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>{icon} Ask {agentLabel} instead</button>}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {showSuggestions && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 4 }}>
            {suggestions.map(s => (
              <button key={s} onClick={() => send(s)} style={{ textAlign: 'left', padding: '11px 13px', borderRadius: 10, border: '1px solid #eef2f7', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151' }}>{s}</button>
            ))}
          </div>
        )}
        {waiting && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '11px 14px', borderRadius: 12, background: '#fff', border: '1px solid #eef2f7', color: '#9ca3af', fontSize: 14 }}>
              <span>{waitingText}</span>
              {waitedSec > 0 && (
                <span style={{ fontSize: 12, fontWeight: 700, color: waitedSec >= 60 ? '#b45309' : '#c7ccd3', fontVariantNumeric: 'tabular-nums' }}>
                  {waitedSec}s{waitedSec >= 60 ? ' · still working' : ''}
                </span>
              )}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Hands-free banner — what it's doing right now, and how to stop it. */}
      {voice.active && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginTop: 10, borderRadius: 12, background: voice.speaking ? '#eff6ff' : '#f0fdf4', border: `1.5px solid ${voice.speaking ? '#bfdbfe' : '#bbf7d0'}` }}>
          <span style={{ fontSize: 18 }}>{voice.speaking ? '🔊' : '🎤'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: voice.speaking ? '#1d4ed8' : '#15803d' }}>
              {voice.speaking ? 'Speaking…' : voice.listening ? 'Listening — go ahead' : 'Ready'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {voice.heard || (voice.speaking ? 'Tap Stop to interrupt' : 'Just talk — it sends when you pause')}
            </div>
          </div>
          <button onClick={voice.stop}
            style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 999, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Stop</button>
        </div>
      )}

      {/* Straight answer to a straight question, sitting right above the keyboard so
          it's the first thing your thumb reaches. Only while the agent's last word is
          the one asking — a new message on either side clears it. */}
      {pendingConfirm && !showingSuggestion && (
        <div style={{ display: 'flex', gap: 10, paddingTop: 12 }}>
          <button onClick={() => send('Yes')} disabled={sending}
            style={{ flex: 1, minHeight: 48, borderRadius: 12, border: 'none', background: sending ? '#9ca3af' : '#16a34a', color: '#fff', fontSize: 16, fontWeight: 800, cursor: sending ? 'default' : 'pointer' }}>
            ✓ Yes
          </button>
          <button onClick={() => send('No')} disabled={sending}
            style={{ flex: 1, minHeight: 48, borderRadius: 12, border: 'none', background: sending ? '#9ca3af' : '#dc2626', color: '#fff', fontSize: 16, fontWeight: 800, cursor: sending ? 'default' : 'pointer' }}>
            ✕ No
          </button>
        </div>
      )}

      {showingSuggestion && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10, fontSize: 12.5, color: '#6d28d9', fontWeight: 700 }}>
          <span style={{ flex: 1 }}>✨ Suggested answer — press Send, or change it</span>
          <button onClick={() => { setInput(''); setSuggested(null); inputRef.current?.focus(); }}
            style={{ background: 'none', border: '1px solid #ddd6fe', borderRadius: 999, padding: '4px 10px', color: '#6d28d9', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Clear</button>
        </div>
      )}
      {/* As you type: matching questions, fewer the more you type. */}
      {typeahead && !sending && !showingSuggestion && (() => {
        const list = typeaheadMatches(input, allAnswers, suggestions);
        if (!list.length) return null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 10 }}>
            {list.map(x => (
              <button key={x.kind + x.text} type="button"
                onClick={() => {
                  if (x.kind === 'answer') {
                    setInput(''); if (inputRef.current) inputRef.current.style.height = 'auto';
                    setLocal(l => [...l.map(y => ({ ...y, state: 'done' })), { id: 'ca_' + Math.random().toString(36).slice(2), q: x.text, row: x.row, at: new Date().toISOString(), state: 'open' }]);
                  } else if (!x.text.includes('…')) send(x.text);
                  else { setInput(x.text.replace('…', '')); inputRef.current?.focus(); }
                }}
                style={{ textAlign: 'left', padding: '10px 13px', borderRadius: 12, border: `1px solid ${x.kind === 'answer' ? '#fde68a' : '#e5e7eb'}`, background: x.kind === 'answer' ? '#fffbeb' : '#fff', fontSize: 14, color: '#1f2937', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
                <span style={{ fontSize: 14 }}>{x.kind === 'answer' ? '📘' : '💬'}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{x.text}</span>
              </button>
            ))}
          </div>
        );
      })()}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border, #e5e7eb)' }}>
        {voice.supported && !voice.active && (
          <button onClick={voice.start} title="Hands-free — talk and it talks back"
            style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: `1px solid ${accent}`, background: '#fff', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎧</button>
        )}
        {/* Grows with what is typed, up to about six lines, so a long message stays readable
            from its first word (Ryan, Oct 1 2026: the one-line box hid the beginning on his
            phone). Enter sends on a keyboard-and-mouse machine; on a phone Enter makes a new
            line and the arrow sends — Shift+Enter is a new line everywhere. */}
        <textarea ref={inputRef} value={input} rows={1} placeholder={placeholder}
          onChange={e => { setInput(e.target.value); const el = e.target; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 160) + 'px'; }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) { e.preventDefault(); ask(); } }}
          disabled={loading || !!err}
          style={{ flex: 1, minHeight: 44, maxHeight: 160, padding: '11px 16px', borderRadius: 22, border: showingSuggestion ? '1.5px solid #a78bfa' : '1px solid var(--border, #e5e7eb)', background: showingSuggestion ? '#faf5ff' : undefined, fontSize: 16, lineHeight: '22px', outline: 'none', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', overflowY: 'auto' }} />
        <button onClick={() => ask()} disabled={!input.trim() || sending || loading} aria-label="Send"
          style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: 'none', background: input.trim() && !sending ? accent : '#e5e7eb', color: '#fff', cursor: input.trim() && !sending ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 9h14M10 3l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
    </div>
  );
}
